import { ConfigConsumer } from '@configProvider/ConfigConsumer';
import { IDebbugable } from '@customTypes/common';
import { TEventBus } from '@eventBus/EventBus';
import bind from '@utils/decorators/bind';

import { IDebuggerConfig } from './Debugger.config';
import { DebuggerEvents } from './Debugger.events';
import type { DebuggerWorker } from './Debugger.worker';
import { ECommand, ILifecycleEvent, TMessage, TModulesInfo } from './IDebug';

export class Debugger extends ConfigConsumer<IDebuggerConfig> {
  private _debuggableModules: Record<string, IDebbugable> = {};
  private _modulesInfoGatheringInterval: ReturnType<typeof globalThis.setInterval> | null = null;
  private _debuggerWorker: DebuggerWorker | null = null;

  private readonly _onLifecycleEventReportedListener: string;

  constructor(eventBus: TEventBus) {
    super('debugger', eventBus);

    this._onLifecycleEventReportedListener = this._eventBus.subscribe(DebuggerEvents.lifecycleEvent, this._onLifecycleEventReported);

    if (this.config.enableRemoteDebugging && this.config.debuggerUrl && this.config.maxWSConnectionRetries) {
      this._startDebugger();
    }
  }

  public registerDebuggableModules(modules: Record<string, IDebbugable>): void {
    this._debuggableModules = { ...this._debuggableModules, ...modules };
  }

  public unregisterDebuggableModules(modules: string[]): void {
    modules.forEach((key) => {
      delete this._debuggableModules[key];
    });
  }

  public destroy(): void {
    super.destroy();

    this._debuggerWorker?.disconnect();

    this._eventBus.unsubscribe(this._onLifecycleEventReportedListener);
  }

  public _handleConfigUpdated(updatedValues: Partial<IDebuggerConfig>): void {
    if (this._debuggerWorker && (updatedValues.debuggerUrl || updatedValues.maxWSConnectionRetries)) {
      this._destroyWorker();
      this._getDebuggerModule();
    }

    if (updatedValues.enableRemoteDebugging === false) {
      this._stopDebugger();
    }

    if (updatedValues.enableRemoteDebugging) {
      this._destroyWorker();
      this._getDebuggerModule();
    }

    if (updatedValues.modulesStateGatheringPeriod) {
      this._disarmSendModulesInfoTimeout();
      this._armSendModulesInfoTimeout();
    }
  }

  private _destroyWorker(): void {
    this._debuggerWorker?.disconnect();
    this._debuggerWorker = null;
  }

  private _startDebugger(): void {
    this._getDebuggerModule();
    this._armSendModulesInfoTimeout();
  }

  private _stopDebugger(): void {
    if (!this._debuggerWorker) {
      return;
    }

    this._debuggerWorker.disconnect();
    this._disarmSendModulesInfoTimeout();
  }

  private async _sendMessage(message: TMessage): Promise<void> {
    const worker: DebuggerWorker = await this._getDebuggerModule();

    worker.sendMessage(message);
  }

  @bind
  private _onConnected(url: string): void {
    this._eventBus.publish(DebuggerEvents.debuggerConnected, url);
    this._armSendModulesInfoTimeout();
  }

  @bind
  private _onDisconnected(): void {
    this._disarmSendModulesInfoTimeout();
  }

  @bind
  private _onLifecycleEventReported(event: ILifecycleEvent): void {
    if (!this._debuggerWorker || !this.config.enableLifecycleEvents) {
      return;
    }

    this._sendMessage({
      command: ECommand.MODULE_LIFECYCLE_EVENT_AVAILABLE,
      event,
    });
  }

  private _armSendModulesInfoTimeout(): void {
    this._modulesInfoGatheringInterval = globalThis.setInterval(this._sendModulesInfo, this.config.modulesStateGatheringPeriod);
  }

  private _disarmSendModulesInfoTimeout(): void {
    if (this._modulesInfoGatheringInterval) {
      globalThis.clearInterval(this._modulesInfoGatheringInterval);
    }
  }

  private _unzipDebuggableModules(modules: Record<string, IDebbugable>): Record<string, IDebbugable> {
    let localModules: Record<string, IDebbugable> = { ...modules };

    Object.values(modules).forEach((module) => {
      if (module.additionalModules) {
        localModules = { ...localModules, ...this._unzipDebuggableModules(module.additionalModules) };
      }
    });

    return localModules;
  }

  @bind
  private _sendModulesInfo(): void {
    const modules: Record<string, IDebbugable> = this._unzipDebuggableModules(this._debuggableModules);
    const modulesMetrics: TModulesInfo = Object.entries(modules).reduce<TModulesInfo>((s, [moduleName, module]) => {
      s[moduleName] = {
        additionalInfo: module.additionalInfo,
        state: module.state,
        groups: module.groups,
        status: module.status,
      };

      return s;
    }, {});

    this._sendMessage({
      command: ECommand.MODULES_INFO_AVAILABLE,
      payload: modulesMetrics,
    });
  }

  private async _getDebuggerModule(): Promise<DebuggerWorker> {
    if (!this._debuggerWorker) {
      const { DebuggerWorker } = await import('./Debugger.worker');
      this._debuggerWorker = new DebuggerWorker();

      this._debuggerWorker.setConfig(this.config.debuggerUrl, this.config.maxWSConnectionRetries, this._onConnected, this._onDisconnected);
      this._debuggerWorker.connect();
    }

    return this._debuggerWorker;
  }
}
